import type { GeocodeNotCalledReason } from '@bandido/shared/types';

/**
 * Geocode de direcciones y POIs (Google, vía ubikeishon — backend #2169,
 * ver `docs/PLACES.md` §9). Lógica pura: sin React, para poder testearse
 * desde un `.ts` (el jest de este repo no resuelve `.tsx` a propósito, ver
 * el docstring de `PlaceAutocomplete.tsx`). Lo consume el campo unificado
 * del punto de traslado (`TransferPointAutocomplete`), que ofrece estas
 * direcciones y los lugares del maestro en el MISMO desplegable (#2227).
 *
 * Es un mecanismo DISTINTO del árbol de lugares (`places.ts`, `plc_*`): acá
 * no hay `plc_*`, no hay cache, y el resultado final es `{name, address,
 * lat, lng}` — el mismo shape que el modo manual de coordenadas de
 * transfers, porque es exactamente lo que reemplaza.
 */

/**
 * Mínimo de caracteres antes de disparar una consulta. El backend acepta
 * menos (su DTO es permisivo, 1..120 — ver `docs/PLACES.md` §9) y lo
 * degrada con `provider.reason: 'too_short'` sin llamar a Google, pero el
 * hook de este componente decide ACÁ cuándo consultar: nunca llega a pasar
 * por esa rama del backend. El mismo mensaje ("seguí escribiendo") se
 * calcula del lado del cliente, sin gastar el roundtrip.
 */
export const GEOCODE_QUERY_MIN_LENGTH = 3;

/** El punto resuelto, en el mismo shape que el modo manual de coordenadas (`PointState` de `TransfersSearchForm`). */
export interface GeocodePointDraft {
  name: string;
  address: string;
  lat: string;
  lng: string;
}

/**
 * "Hotel Nacional, Av. Italia 1234, Montevideo, Uruguay" → nombre = primer
 * segmento (antes de la primera coma), dirección = la descripción entera.
 * Es lo único que hay: Google no publica un campo "nombre" separado de
 * "dirección" en el paso 1 (`GeocodePrediction.description` es un solo
 * string), y ninguno de los dos pasos del geocode da un nombre estructurado.
 */
export function splitGeocodeDescription(description: string): {
  name: string;
  address: string;
} {
  const trimmed = description.trim();
  const [firstSegment] = trimmed.split(',');
  const name = firstSegment?.trim() || trimmed;
  return { name, address: trimmed };
}

/**
 * Por qué el picker no tiene nada útil para mostrar ahora — mismo espíritu
 * que `placesNoticeReason` (`places.ts`) pero con el vocabulario propio de
 * geocode. `notEnabled`/`budgetExceeded` salen de `provider.reason` (un
 * `200` válido, nunca una excepción — ver docs/PLACES.md §9) y son las dos
 * razones que ameritan ofrecer el fallback de carga manual explícito
 * (`geocodeShouldOfferManualFallback`); `tooShort`/`empty` no.
 */
export type GeocodeNoticeReason =
  | 'tooShort'
  | 'error'
  | 'notEnabled'
  | 'budgetExceeded'
  | 'empty';

export function geocodeNoticeReason({
  queryLength,
  isError,
  providerReason,
}: {
  queryLength: number;
  isError: boolean;
  providerReason?: GeocodeNotCalledReason;
}): GeocodeNoticeReason {
  if (isError) return 'error';
  if (queryLength < GEOCODE_QUERY_MIN_LENGTH) return 'tooShort';
  if (providerReason === 'not_enabled') return 'notEnabled';
  if (providerReason === 'budget_exceeded') return 'budgetExceeded';
  return 'empty';
}

/**
 * ¿Esta razón amerita mostrarle al asesor la salida explícita a carga
 * manual (no solo "sin resultados, seguí escribiendo")? Cupo agotado o
 * geocode no habilitado son fallas de SERVICIO, no de la búsqueda — el
 * asesor no puede arreglarlas escribiendo distinto, necesita el fallback.
 */
export function geocodeShouldOfferManualFallback(
  reason: GeocodeNoticeReason,
): boolean {
  return reason === 'error' || reason === 'notEnabled' || reason === 'budgetExceeded';
}

/**
 * Un fallo del paso 2 (`resolve`) SIEMPRE ofrece la carga manual.
 *
 * No hay lista de códigos: el asesor ya eligió una predicción, así que no hay
 * nada que pueda escribir distinto para arreglarlo — ni con el cupo agotado,
 * ni con el gate apagado a mitad de sesión (`PLACES_NOT_ENABLED`), ni con un
 * error de transporte que ni siquiera trae código de la API. Enumerar los
 * códigos "que ameritan fallback" invertía la carga de la prueba: cualquiera
 * que faltara —y faltaban— dejaba al asesor mirando "no se pudo resolver" sin
 * ninguna salida, con el punto del traslado sin cargar.
 *
 * La invariante es al revés: la carga manual no desaparece nunca porque el
 * geocode falle. Si algún día aparece un fallo que el asesor SÍ pueda
 * corregir con su input, se excluye ESE, con evidencia.
 */
export function geocodeResolveErrorOffersManualFallback(
  resolveFailed: boolean,
): boolean {
  return resolveFailed;
}

/**
 * ¿La respuesta del `resolve` es del MISMO lugar que se pidió?
 *
 * El paso 2 se pide por `placeId` y la respuesta lo devuelve, pero el punto
 * que se arma mezcla DOS fuentes: la descripción de la predicción elegida
 * (nombre y dirección para mostrar) y las coordenadas de la respuesta. Si por
 * un desfasaje del proveedor esas dos fuentes no son el mismo lugar, queda un
 * punto de traslado cuya dirección dice una cosa y cuyas coordenadas apuntan a
 * otra — y el traslado se reserva contra las COORDENADAS.
 *
 * El backend valida cada campo de la respuesta por separado, pero no que la
 * identidad devuelta sea la pedida: eso solo lo puede chequear quien sabe qué
 * pidió.
 */
export function resolvedPointMatchesPrediction(
  requestedPlaceId: string,
  resolvedPlaceId: string | undefined,
): boolean {
  return Boolean(resolvedPlaceId) && resolvedPlaceId === requestedPlaceId;
}

// ── Modo del punto de coordenadas (buscador vs. campos manuales) ──────────
// Mismo principio que `resolvePlacesPickerMode` (`placesPickerMode.ts`, el
// picker hermano del epic #2167): "una vez que hay algo elegido en un modo,
// ese modo queda fijo" — generalizado acá a "una vez que el asesor
// INTERACTUÓ con el modo manual (lo pidió, tipeó en un campo, o restauró un
// punto con valor), queda fijo", para que el buscador no se le aparezca
// debajo de datos que ya cargó (a mano o por geocode). Extraído a función
// pura para poder testear el invariante sin un test de `.tsx` (el jest de
// este repo no resuelve `.tsx` a propósito).

/**
 * `useManualFields` inicial al restaurar un punto desde la URL: `true` si
 * ya trae dirección+coordenadas (no hay nada que "buscar", mostrarlo en el
 * buscador vacío sería peor) — `false` si no hay nada, así el buscador es
 * lo primero que ve el asesor cuando el setting está prendido.
 */
export function deriveInitialUseManualFields({
  address,
  lat,
  lng,
}: {
  address: string;
  lat: string;
  lng: string;
}): boolean {
  return Boolean(address.trim() && lat.trim() && lng.trim());
}

/**
 * ¿Se muestran los campos editables de nombre/dirección/lat/lng en vez del
 * buscador? `useManualFields` manda si ya es `true` (sticky); si no, sigue
 * al setting EN VIVO — antes de que el asesor interactúe con el modo
 * manual, es correcto que el picker siga al setting resolviendo async o
 * cambiando en caliente (mismo criterio que `resolvePlacesPickerMode` antes
 * de la primera selección).
 */
export function shouldShowManualCoordsFields({
  useManualFields,
  placesEnabled,
}: {
  useManualFields: boolean;
  placesEnabled: boolean;
}): boolean {
  return useManualFields || !placesEnabled;
}
